import { Booking, Vehicle } from '@/types';
import { currencySymbol } from '@/lib/currency';

export const MOROCCAN_LOCATIONS = [
  'Agadir Airport (AGA)',
  'Agadir City Center',
  'Marrakech Airport (RAK)',
  'Marrakech City Center',
  'Casablanca Airport (CMN)',
  'Casablanca City Center',
  'Rabat Airport (RBA)',
  'Tanger Airport (TNG)',
  'Fes Airport (FEZ)',
  'Essaouira Center',
];

export const PIPELINE_STEPS = [
  'pending',
  'confirmed',
  'active',
  'returned',
  'completed',
] as const;

export function formatMAD(n: number): string {
  return `${Number(n || 0).toLocaleString('fr-MA')} DH`;
}

export function daysUntil(dateStr?: string): number | null {
  if (!dateStr) return null;
  const target = new Date(dateStr + 'T23:59:59');
  if (isNaN(target.getTime())) return null;
  const now = new Date();
  const diff = target.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export type ComplianceState = 'ok' | 'soon' | 'expired' | 'missing';

export function complianceState(dateStr?: string, warnDays = 30): ComplianceState {
  if (!dateStr) return 'missing';
  const d = daysUntil(dateStr);
  if (d === null) return 'missing';
  if (d < 0) return 'expired';
  if (d <= warnDays) return 'soon';
  return 'ok';
}

export function vidangeState(vehicle: Vehicle): ComplianceState {
  if (!vehicle.nextVidangeKm) return 'missing';
  const remaining = vehicle.nextVidangeKm - vehicle.mileage;
  if (remaining <= 0) return 'expired';
  if (remaining <= 1000) return 'soon';
  return 'ok';
}

export function normalizePhoneForWhatsApp(phone: string): string {
  // Strip everything except digits, keep leading country code if present.
  // Moroccan mobiles: 06XX -> 2126XX
  let digits = (phone || '').replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.length === 10 && digits.startsWith('0')) digits = '212' + digits.slice(1);
  if (digits.length === 9 && /^[67]/.test(digits)) digits = '212' + digits;
  return digits;
}

export function buildWhatsAppLink(phone: string, message: string): string {
  const to = normalizePhoneForWhatsApp(phone);
  return `https://wa.me/${to}?text=${encodeURIComponent(message)}`;
}

export function bookingWhatsAppText(
  booking: Booking,
  template: string,
  sender = 'fleetava',
  currency?: string | null,
): string {
  const custom = template.trim();
  if (custom === '') return buildBookingWhatsAppMessage(booking, sender, currency);
  return custom
    .replaceAll('{name}', booking.customerName)
    .replaceAll('{id}', booking.id)
    .replaceAll('{status}', booking.status.toUpperCase())
    .replaceAll('{vehicle}', booking.vehicleName)
    .replaceAll('{pickup}', booking.pickupLocation)
    .replaceAll('{date}', booking.pickupDate)
    .replaceAll('{time}', booking.pickupTime)
    .replaceAll('{agency}', sender);
}

export function buildBookingWhatsAppMessage(
  b: Booking,
  sender = 'fleetava Agadir',
  currency?: string | null,
): string {
  const sym = currencySymbol(currency);
  const lines = [
    `Bonjour ${b.customerName},`,
    `Votre réservation ${b.id} est ${b.status.toUpperCase()}.`,
    `Véhicule: ${b.vehicleName}`,
    `Retrait: ${b.pickupLocation} le ${b.pickupDate} à ${b.pickupTime}`,
    `Retour: ${b.dropoffLocation} le ${b.dropoffDate} à ${b.dropoffTime}`,
    `Total: ${b.totalPrice} ${sym}${b.avanceAmount ? ` (Avance: ${b.avanceAmount} ${sym}, Reste: ${b.totalPrice - b.avanceAmount} ${sym})` : ''}${b.cautionAmount ? ` | Caution: ${b.cautionAmount} ${sym}` : ''}`,
    `Merci — ${sender}`,
  ];
  return lines.join('\n');
}

export function buildReturnReminderMessage(b: Booking, sender = 'fleetava'): string {
  return `Bonjour ${b.customerName}, rappel: retour du véhicule ${b.vehicleName} (${b.id}) prévu le ${b.dropoffDate} à ${b.dropoffTime} à ${b.dropoffLocation}. Merci de prévoir le solde restant. — ${sender}`;
}

export function bookingOverlaps(
  aPickup: string,
  aDrop: string,
  bPickup: string,
  bDrop: string
): boolean {
  // ISO date strings compare lexicographically
  return aPickup <= bDrop && bPickup <= aDrop;
}

export function isVehicleDoubleBooked(
  vehicleId: string,
  pickup: string,
  dropoff: string,
  bookings: Booking[],
  ignoreId?: string
): Booking | null {
  for (const b of bookings) {
    if (b.vehicleId !== vehicleId) continue;
    if (ignoreId && b.id === ignoreId) continue;
    if (b.status === 'cancelled' || b.status === 'completed' || b.status === 'returned') continue;
    if (bookingOverlaps(pickup, dropoff, b.pickupDate, b.dropoffDate)) return b;
  }
  return null;
}
